import type { Valhalla_CostingModel } from "@/api/tripPlanning/types/valhallaTypes";
import type { VehicleDTO } from "@/api/vtnz-backend/vehicles";
import generateUniqueLocalID from "@/utils/generateUniqueLocalID";
import { VehicleICE } from "../ICE/vehicleICEClass";
import { type FuelType, type Vehicle, type VehicleSource } from "../types";

export type VehicleCoreOptions = {
  /** The external id of the vehicle. */
  externalId?: string;
  /** The licenses plate of the vehicle. */
  licensePlate?: string;
  /** vehicle identification number. */
  vin?: string;
  /** describes where the VIN is located. */
  vinLocation?: string;
  /** The name of the vehicle. */
  name?: string;
  /** File paths for images related to this vehicles. */
  imageSrcPaths?: string[];
  /** The fuel type of the vehicle. */
  fuelType?: FuelType;
  /**
   * Indicates how this vehicle exists within the system.
   *
   * Determines the rules that apply to the vehicle, such as persistence,
   * external linking, and whether it represents a real-world vehicle record
   * or a planning-only vehicle configuration.
   */
  vehicleSource: VehicleSource;
  /**
   * The valhalla costing type for this vehicle.
   *
   * This is used for routing and trip planning. It is set to "auto" by default, but can be changed to "truck", "bus", "bicycle", or "taxi" depending on the vehicle type.
   */
  valhallaCostingType: Valhalla_CostingModel;
  vehicleClass?: string;
  numberOfAxles?: number;
  registrationExpiry?: string;
  wofCofNumber?: string;
  wofCofExpiry?: string;
  /** Transport Service Licence required in New Zealand for operating commercial passenger, goods (over 6,000 kg GVM), vehicle recovery, or rental services */
  tslRequired?: boolean;
  /** Gross vehicle mass in kg - the maximum legal weight a vehicle can safely reach when fully loaded */
  gvm?: number;
  maxAirPressure?: string;
  /** On a vehicle tyre placard, "3/5" typically represents the passenger load (3 occupants vs. 5 occupants), which determines your required tyre pressure */
  pressure35Ratio?: string;
  tractorTrailerRegistration?: string;
  /** Has a compensator cap been installed on this vehicle.
   *
   * A "boost comp cap" (compensator cap) for vehicles is an aftermarket engine part used on diesel mechanical injection pumps, specifically common VE-style pump configurations. It is designed to solve tuning limitations and reduce black smoke during start-up and boost delivery
   */
  compCap?: boolean;
  /** Low Pressure Warning Device testing on heavy vehicle brake systems during a Certificate of Fitness */
  lpwdRequired?: boolean;
  aux?: string;
  applications?: string;
  /** Foreign key into `locations.id` - this vehicle's assigned home/depot location, if one is set. */
  homeLocationExternalId?: string;

  /** id of user that created this vehicle, if null assume system */
  createdBy: string | null;

  /** ico date time string for when this was created.  */
  createdAt: string;
};

export abstract class VehicleCore {
  // -------------------------------------------------------------------- //
  // ------------------------- Global class state ----------------------- //
  // -------------------------------------------------------------------- //

  // global record of class instance ids this session.
  static usedIds: string[] = [];

  // -------------------------------------------------------------------- //
  // ------------------------------- State ------------------------------ //
  // -------------------------------------------------------------------- //

  // - vehicle identification - //

  /** local unique id. */
  id: string;
  
  /** The external id of the vehicle. */
  externalId: string | null;
  
  /** The licenses plate of the vehicle. */
  licensePlate: string | null;
  
  /** vehicle identification number. */
  VIN: string | null;
  
  /** describes where the VIN is located. */
  vinLocation: string | null;

  /** The name of the vehicle. */
  name: string | null;
  
  /**
   * Indicates how this vehicle exists within the system.
   *
   * Determines the rules that apply to the vehicle, such as persistence,
   * external linking, and whether it represents a real-world vehicle record
   * or a planning-only vehicle configuration.
   */
  vehicleSource: VehicleSource;

  // - vehicle configuration - //

  /** The fuel type of the vehicle. */
  fuelType: FuelType | null;

  // - misc - //

  /** File paths for images related to this vehicles. */
  imageSrcPaths: string[] = [];

  /**
   * The valhalla costing type for this vehicle.
   *
   * This is used for routing and trip planning. It is set to "auto" by default, but can be changed to "truck", "bus", "bicycle", or "taxi" depending on the vehicle type.
   */
  valhallaCostingType: Valhalla_CostingModel;

  vehicleClass: string | null;
  numberOfAxles: number | null;
  registrationExpiry: string | null;
  wofCofNumber: string | null;
  wofCofExpiry: string | null;
  
  /** Transport Service Licence required in New Zealand for operating commercial passenger, goods (over 6,000 kg GVM), vehicle recovery, or rental services */
  tslRequired: boolean | null;
  
  /** Gross vehicle mass in kg - the maximum legal weight a vehicle can safely reach when fully loaded */
  gvm: number | null;
  maxAirPressure: string | null;
  
  /** On a vehicle tyre placard, "3/5" typically represents the passenger load (3 occupants vs. 5 occupants), which determines your required tyre pressure */
  pressure35Ratio: string | null;
  
  tractorTrailerRegistration: string | null;
  
  /** Has a compensator cap been installed on this vehicle.
   *
   * A "boost comp cap" (compensator cap) for vehicles is an aftermarket engine part used on diesel mechanical injection pumps, specifically common VE-style pump configurations. It is designed to solve tuning limitations and reduce black smoke during start-up and boost delivery
   */
  compCap: boolean;
  
  /** Low Pressure Warning Device testing on heavy vehicle brake systems during a Certificate of Fitness */
  lpwdRequired: boolean;
  
  aux: string | null;

  applications: string | null;

  /** Foreign key into `locations.id` - this vehicle's assigned home/depot location, if one is set. */
  homeLocationExternalId: string | null;

  /** ico date time string for when this was created.  */
  createdAt: string;
  
  /** id of user that created this vehicle, if null assume system */
  createdBy: string | null;

  // -------------------------------------------------------------------- //
  // ---------------------------- Constructor --------------------------- //
  // -------------------------------------------------------------------- //

  constructor({
    externalId,
    imageSrcPaths,
    licensePlate,
    vin,
    vinLocation,
    fuelType,
    vehicleSource,
    name,
    valhallaCostingType,
    compCap,
    createdAt,
    createdBy,
    lpwdRequired,
    applications,
    aux,
    homeLocationExternalId,
    gvm,
    maxAirPressure,
    numberOfAxles,
    pressure35Ratio,
    registrationExpiry,
    tractorTrailerRegistration,
    tslRequired,
    vehicleClass,
    wofCofExpiry,
    wofCofNumber,
  }: VehicleCoreOptions) {
    this.id = generateUniqueLocalID(VehicleCore.usedIds, "vehicle");
    this.externalId = externalId ?? null;
    this.licensePlate = licensePlate ?? null;
    this.VIN = vin ?? null;
    this.vinLocation = vinLocation ?? null
    this.fuelType = fuelType ?? null;
    this.vehicleSource = vehicleSource;
    this.imageSrcPaths = imageSrcPaths ?? [];
    this.name = name ?? null;
    this.valhallaCostingType = valhallaCostingType;
    this.compCap = compCap ?? false;
    this.createdAt = createdAt;
    this.createdBy = createdBy;
    this.lpwdRequired = lpwdRequired ?? false
    this.applications = applications ?? null
    this.aux = aux ?? null
    this.homeLocationExternalId = homeLocationExternalId ?? null
    this.gvm = gvm ?? null
    this.maxAirPressure = maxAirPressure ?? null
    this.numberOfAxles = numberOfAxles ?? null
    this.pressure35Ratio = pressure35Ratio ?? null
    this.registrationExpiry = registrationExpiry ?? null
    this.tractorTrailerRegistration = tractorTrailerRegistration ?? null
    this.tslRequired = tslRequired ?? false
    this.vehicleClass = vehicleClass ?? null
    this.wofCofExpiry = wofCofExpiry ?? null
    this.wofCofNumber = wofCofNumber ?? null


    VehicleCore.usedIds.push(this.id);
  }

  static fromDTO(dto: VehicleDTO): Vehicle {
    switch (dto.fuel_type) {
      case "DIESEL":
      case "PETROL":
      case "HYBRID":
        return VehicleICE.fromDTO(dto);
      case "ELECTRIC":
      case "HYDROGEN":
      case "PHEV":
      case "OTHER":
      default:
        throw new Error("Unsupported vehicle type");
    }
  }

  // ----------------------------------------------------------------------- //
  // -------------------------------- Getters ------------------------------ //
  // ----------------------------------------------------------------------- //

  public get fuelTypeDisplayText() {
    switch (this.fuelType) {
      case "DIESEL":
        return "Diesel";
      case "ELECTRIC":
        return "Electric";
      case "HYBRID":
        return "Hybrid";
      case "HYDROGEN":
        return "Hydrogen";
      case "OTHER":
        return "Other";
      case "PETROL":
        return "Petrol";
      case "PHEV":
        return "Plug in hybrid";
      default:
        return "Unknown";
    }
  }

  /**
   * Gets the fuel type icon based on the fuel type
   * @returns mdi-fuel for diesel/petrol, custom:offlineBolt for electric, custom:hydrogen for hydrogen, custom:waterEc for hybrid/plug in hybrid, empty string for unknown
   */
  public get fuelTypeIcon() {
    switch (this.fuelType) {
      case "DIESEL":
      case "PETROL":
        return "mdi-fuel";
      case "ELECTRIC":
        return "custom:offlineBolt";
      case "HYDROGEN":
        return "custom:hydrogen";
      case "HYBRID":
      case "PHEV":
        return "custom:waterEc";
      default:
        return "";
    }
  }

  /**
   * Gets the icon representing the fuel capacity based on the fuel type.
   * @returns mdi-meter-gas-outline for diesel/petrol/hydrogen, custom:battery for electric/hybrid/plug in hybrid, empty string for unknown.
   */

  public get fuelCapacityIcon() {
    switch (this.fuelType) {
      case "DIESEL":
      case "PETROL":
        return "mdi-meter-gas-outline";
      case "ELECTRIC":
        return "custom:battery";
      case "HYDROGEN":
        return "mdi-meter-gas-outline";
      case "HYBRID":
      case "PHEV":
        return "custom:battery";
      default:
        return "";
    }
  }

  abstract get fuelCapacityDisplayText(): string | null;
}
